/**
 * Feature Heatmap — grille experts × features applicatives.
 * Montre qui maîtrise quoi sur une application donnée (scores from user_year_feature_scores).
 */
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { analyticsApi, applicationApi, type ApplicationDto, type FeatureHeatmapResult } from "../api/api";

// ── Score color helpers ───────────────────────────────────────────────────────
function scoreColor(score: number | null, target: number): { bg: string; text: string } {
  if (score === null) return { bg: "#f8fafc", text: "#94a3b8" };
  if (score >= target) {
    if (score === 4) return { bg: "#16a34a", text: "#fff" };
    return { bg: "#86efac", text: "#14532d" };
  }
  if (score === 0) return { bg: "#fca5a5", text: "#7f1d1d" };
  if (score === 1) return { bg: "#fdba74", text: "#7c2d12" };
  return { bg: "#fde68a", text: "#713f12" }; // score 2
}

function avgColor(avg: number | null, target: number): string {
  if (avg === null) return "#94a3b8";
  if (avg >= target) return "#16a34a";
  if (avg >= target - 1) return "#f59e0b";
  return "#ef4444";
}

// ── Component ─────────────────────────────────────────────────────────────────
export function FeatureHeatmapPage() {
  const { t, i18n } = useTranslation();
  const langFr = i18n.language?.toLowerCase().startsWith("fr");

  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [applicationId, setApplicationId] = useState("");
  const [moduleCode, setModuleCode] = useState("");
  const [applications, setApplications] = useState<ApplicationDto[]>([]);
  const [data, setData] = useState<FeatureHeatmapResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Load applications
  useEffect(() => {
    applicationApi.list().then((r) => setApplications(r.applications)).catch(() => {});
  }, []);

  // Load heatmap when filters change
  useEffect(() => {
    if (!applicationId) { setData(null); return; }
    setLoading(true);
    setError("");
    analyticsApi.featureHeatmap({
      year,
      applicationId,
      moduleCode: moduleCode || undefined,
    })
      .then(setData)
      .catch((e) => setError(e.message ?? "Error"))
      .finally(() => setLoading(false));
  }, [year, applicationId, moduleCode]);

  // Available modules from data
  const modules = useMemo(() => data?.modules ?? [], [data]);
  const moduleOptions = useMemo(() =>
    modules.filter((m) => m.moduleCode).map((m) => ({ code: m.moduleCode!, label: m.moduleLabel ?? m.moduleCode! })),
    [modules]
  );

  // Feature count
  const totalFeatures = useMemo(() =>
    modules.flatMap((m) => m.subModules).flatMap((s) => s.features).length,
    [modules]
  );

  return (
    <div className="sm-page">
      <div className="sm-page-header">
        <h1 className="sm-page-title">{t("feature_heatmap_title", "Application Skills Heatmap")}</h1>
        <p className="sm-muted">{t("feature_heatmap_sub", "Score grid: experts × application features")}</p>
      </div>

      {/* Filters */}
      <div className="sm-card sm-card-pad" style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 16 }}>
        <label className="sm-label">
          {t("year")}
          <select className="sm-select" value={year} onChange={(e) => setYear(Number(e.target.value))}>
            {[currentYear, currentYear - 1, currentYear - 2].map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </label>

        <label className="sm-label">
          {t("application")}
          <select className="sm-select" value={applicationId} onChange={(e) => { setApplicationId(e.target.value); setModuleCode(""); }}>
            <option value="">{t("select_application", "Select application…")}</option>
            {applications.map((a) => (
              <option key={a.id} value={a.id}>{a.label}</option>
            ))}
          </select>
        </label>

        {moduleOptions.length > 0 && (
          <label className="sm-label">
            {t("module", "Module (N1)")}
            <select className="sm-select" value={moduleCode} onChange={(e) => setModuleCode(e.target.value)}>
              <option value="">{t("all_modules", "All modules")}</option>
              {moduleOptions.map((m) => (
                <option key={m.code} value={m.code}>{m.code} — {m.label}</option>
              ))}
            </select>
          </label>
        )}
      </div>

      {/* Error */}
      {error && <div className="sm-alert sm-alert-error">{error}</div>}

      {/* No app selected */}
      {!applicationId && (
        <div className="sm-card sm-card-pad sm-muted" style={{ textAlign: "center", padding: 40 }}>
          {t("select_application_hint", "Select an application to display the heatmap.")}
        </div>
      )}

      {/* Loading */}
      {loading && applicationId && (
        <div className="sm-card sm-card-pad sm-muted" style={{ textAlign: "center", padding: 40 }}>
          {t("loading", "Loading…")}
        </div>
      )}

      {/* Heatmap */}
      {!loading && data && (
        <>
          {/* Summary bar */}
          <div className="sm-card sm-card-pad" style={{ display: "flex", gap: 24, flexWrap: "wrap", marginBottom: 12, alignItems: "center" }}>
            <span style={{ fontWeight: 600 }}>{data.applicationLabel} — {data.year}</span>
            <span className="sm-muted">{data.experts.length} {t("experts", "experts")}</span>
            <span className="sm-muted">{totalFeatures} {t("features", "features")}</span>
            <span style={{ color: "#1d4ed8", fontSize: "0.85em" }}>
              {t("target_label", "Target")}: <strong>≥ {data.target}</strong>
            </span>
          </div>

          {/* Legend */}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12, fontSize: "0.78em" }}>
            {[
              { bg: "#fca5a5", text: "#7f1d1d", label: "0" },
              { bg: "#fdba74", text: "#7c2d12", label: "1" },
              { bg: "#fde68a", text: "#713f12", label: "2" },
              { bg: "#86efac", text: "#14532d", label: "3" },
              { bg: "#16a34a", text: "#fff", label: "4" },
              { bg: "#f8fafc", text: "#94a3b8", label: "—" },
            ].map((c) => (
              <span key={c.label} style={{
                background: c.bg, color: c.text,
                padding: "2px 8px", borderRadius: 4, fontWeight: 600,
              }}>
                {c.label}
              </span>
            ))}
          </div>

          {/* Table */}
          {modules.map((mod) => (
            <div key={mod.moduleCode ?? "__none__"} className="sm-card" style={{ marginBottom: 16, overflow: "hidden" }}>
              {/* Module header */}
              <div style={{
                background: "#1e40af", color: "#fff",
                padding: "8px 16px", fontWeight: 700, fontSize: "0.95em",
              }}>
                {mod.moduleCode ? `${mod.moduleCode} — ${mod.moduleLabel ?? ""}` : t("no_module", "No module")}
              </div>

              {mod.subModules.map((sub) => (
                <div key={sub.subModuleCode ?? "__none__"}>
                  {/* Sub-module header */}
                  <div style={{
                    background: "#dbeafe", color: "#1e40af",
                    padding: "4px 16px", fontWeight: 600, fontSize: "0.82em",
                    borderBottom: "1px solid #bfdbfe",
                  }}>
                    {sub.subModuleCode ? `${sub.subModuleCode} — ${sub.subModuleLabel ?? ""}` : t("no_submodule", "No sub-module")}
                  </div>

                  {/* Feature rows table */}
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.82em" }}>
                      <thead>
                        <tr style={{ background: "#f8fafc" }}>
                          <th style={{ textAlign: "left", padding: "4px 12px", minWidth: 180, fontWeight: 600, borderBottom: "1px solid #e2e8f0", position: "sticky", left: 0, background: "#f8fafc", zIndex: 1 }}>
                            {t("feature", "Feature")}
                          </th>
                          <th style={{ padding: "4px 8px", fontWeight: 600, borderBottom: "1px solid #e2e8f0", whiteSpace: "nowrap", color: "#64748b" }}>
                            {t("avg", "Avg")}
                          </th>
                          <th style={{ padding: "4px 8px", fontWeight: 600, borderBottom: "1px solid #e2e8f0", whiteSpace: "nowrap", color: "#64748b" }}>
                            {t("coverage_pct", "Cov.")}
                          </th>
                          {data.experts.map((exp) => (
                            <th key={exp.id} style={{
                              padding: "4px 6px", fontWeight: 500,
                              borderBottom: "1px solid #e2e8f0",
                              whiteSpace: "nowrap", maxWidth: 80, overflow: "hidden",
                              textOverflow: "ellipsis", fontSize: "0.78em", color: "#374151",
                            }} title={exp.name}>
                              {exp.name.split(" ").map((w) => w[0]).join("").slice(0, 3)}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {sub.features.map((feat) => {
                          const covPct = feat.totalExperts > 0
                            ? Math.round((feat.coveredCount / feat.totalExperts) * 100)
                            : 0;
                          return (
                            <tr key={feat.featureId} style={{ borderBottom: "1px solid #f1f5f9" }}>
                              {/* Feature label */}
                              <td style={{
                                padding: "4px 12px",
                                position: "sticky", left: 0,
                                background: "#fff", zIndex: 1,
                              }}>
                                <span style={{
                                  fontFamily: "monospace", fontSize: "0.85em",
                                  background: "#f1f5f9", padding: "1px 5px",
                                  borderRadius: 3, marginRight: 6, color: "#475569",
                                  fontWeight: 600,
                                }}>
                                  {feat.featureCode}
                                </span>
                                <span style={{ color: "#374151" }}>
                                  {langFr && feat.featureLabelFr ? feat.featureLabelFr : feat.featureLabelEn}
                                </span>
                              </td>

                              {/* Avg */}
                              <td style={{ padding: "4px 8px", textAlign: "center", fontWeight: 700, color: avgColor(feat.avg, data.target) }}>
                                {feat.avg !== null ? feat.avg.toFixed(1) : "—"}
                              </td>

                              {/* Coverage % */}
                              <td style={{ padding: "4px 8px", textAlign: "center", fontSize: "0.85em",
                                color: covPct >= 80 ? "#16a34a" : covPct >= 50 ? "#f59e0b" : "#ef4444" }}>
                                {feat.totalExperts > 0 ? `${covPct}%` : "—"}
                              </td>

                              {/* Score cells */}
                              {data.experts.map((exp) => {
                                const score = feat.scores[exp.id] ?? null;
                                const { bg, text } = scoreColor(score, data.target);
                                return (
                                  <td key={exp.id} style={{
                                    padding: "4px 6px", textAlign: "center",
                                    background: bg, color: text,
                                    fontWeight: score !== null ? 700 : 400,
                                    fontSize: "0.9em",
                                  }}>
                                    {score !== null ? score : "·"}
                                  </td>
                                );
                              })}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          ))}

          {/* Empty state */}
          {modules.length === 0 && (
            <div className="sm-card sm-card-pad sm-muted" style={{ textAlign: "center", padding: 40 }}>
              {t("no_features_for_application", "No features found for this application.")}
            </div>
          )}
        </>
      )}
    </div>
  );
}
